import DIWFavorite2Basket from '@/components/diw.favorite2basket';
import TKBasketAction, { BasketAction, BasketData } from '@tk/utilities/tk.basket.action';
import { sendNotification, sendNotificationByItem, NotificationType } from '@tk/utilities/tk.notification';
import { setAsyncUrl, fetchRequest } from '@tk/utilities/tk.fetch';
import TKBasketButton from '@tk/components/tk.basket.button';
import { basketMessages } from '@tk/utilities/tk.messages';

interface BasketResponse {
    currency: string;
    amount: string;
    itemCount: string;
    shipping: string;
}

export default class DIWBasketAction extends TKBasketAction {
    constructor(component: HTMLElement) {
        super(component);
    }

    async fetch(checkForReadonlyContext = false) {
        const payload = this.prepareDataSubmission();
        const favorite2basket = this.component.closest('diw-favorite-to-basket') as DIWFavorite2Basket;

        const giftCardSelect: HTMLSelectElement | null = document.querySelector('#giftcard-value');

        if (giftCardSelect) {
            payload['saldocitem.netsp'] = giftCardSelect.value;
            payload['saldocitem.grosssp'] = giftCardSelect.value;
            payload['saldocitem.free7'] = giftCardSelect.value;
        }

        if (payload.action === 'addall' && favorite2basket.isActive) {
            await favorite2basket.fetch();
            const basketButton = (this.component as TKBasketButton).button;
            basketButton && (basketButton.disabled = false);
            sendNotification(NotificationType.SUCCESS, basketMessages.messageAddedAllToBasket.text);
            return;
        }

        const url = setAsyncUrl(checkForReadonlyContext);
        this.emit({ action: BasketAction.FETCHING });
        fetchRequest({
            requestURL: url,
            resolveHandler: this.refreshResultList.bind(this),
            payload,
        });
    }

    refreshResultList(response: TKResponse<BasketData>) {
        this.emit({
            action: BasketAction.REFRESHED_LIST,
            data: response.dataAsJson,
        });
        if (!response.success) return;
        this.updateMiniBasket(response.dataAsJson);
        this.updateBasket(response.dataAsJson);
        if (response.dataAsJson.action === 'added') {
            sendNotificationByItem(this.getQuantityMessage());
        }
        if (response.dataAsJson.action === 'addedAll') {
            const favorite2basket = this.component.closest('diw-favorite-to-basket') as DIWFavorite2Basket;
            favorite2basket && favorite2basket.fetch();
        }

        const { action } = response.dataAsJson;
        this.updateCartTracker(action);
    }

    updateBasket(data: BasketData) {
        if (!this.basketList) return;

        const { tkAction } = this.component.dataset;
        if (tkAction === 'delete' || tkAction === 'deleteall') {
            if (data.quantity === 0) {
                window.location.reload();
            } else {
                const basketPos =
                    this.basketList.querySelector(`[data-tk-internalno="${this.component.dataset.tkInternalno}"]`) ||
                    this.component.closest('[data-tk-basket-item]');
                if (basketPos) basketPos.remove();
            }
        } else if (tkAction === 'change') {
            // update item price and amount
            const price = this.component.closest('[data-tk-basket-item]')?.querySelector('[data-tk-basket-item-price]');
            if (price) {
                price.innerHTML = `${data.currency} ${data.itemPrice}`;
            }
            const amount = this.component
                .closest('[data-tk-basket-item]')
                ?.querySelector('[data-tk-basket-item-amount]');
            if (!amount) return;
            amount.innerHTML = `${data.currency} ${data.itemAmount}`;
        } else if (this.component.closest('tk-quick-order')) {
            // update after quickorder
            const quickorder = this.component.closest<HTMLElement>('tk-quick-order');
            if (!quickorder) return;
            fetchRequest({
                requestURL: quickorder.dataset.tkUrlRefresh || window.location.href,
                resolveHandler: this.refreshBasketList.bind(this),
            });
        }
        // update total amount and VAT
        if (this.totalAmount) {
            const url = this.totalAmount.getAttribute('data-tk-basket-amount-url');
            if (url) {
                fetchRequest({
                    requestURL: url,
                    resolveHandler: this.refreshBasketTotal.bind(this),
                });
            } else {
                this.totalAmount.innerHTML = `${data.currency} ${data.totalAmount}`;
            }
        }
        const totalVat = this.basketList.querySelector('[data-tk-basket-total-vat]');
        if (totalVat) {
            totalVat.innerHTML = `${data.currency} ${data.totalVat}`;
        }

        this.emit({ action: BasketAction.REFRESHED_BASKET });
    }

    refreshBasketTotal(response: TKResponse) {
        if (!response || !response.dataAsJson) return;
        const data = response.dataAsJson as BasketResponse;

        this.totalAmount!.innerHTML = `${data.currency} ${data.amount}`;

        // update shipping fee
        const shipping = document.querySelector('[data-tk-shipping-fee]');
        if (shipping) shipping.innerHTML = data.shipping;

        // update article count
        const itemCount = document.querySelector('[data-tk-basket-icon-count]');
        if (itemCount) itemCount.innerHTML = data.itemCount;
    }
}
